import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
  notifySnapTradeLinkComplete,
  SNAPTRADE_LINK_CHANNEL,
} from '../../pages/SnapTradeCallbackPage';
import {
  createLinkSession,
  createSelectedBrokerageAccounts,
  getAccounts,
  getConnections,
  getNormalizationPreview,
  getRawAccountPreview,
  importReviewedBrokerageData,
  linkExistingBrokerageAccount,
  BrokerageImportReviewedResponse,
  BrokerageNormalizationPreviewAccount,
  BrokerageNormalizationPreviewResponse,
  BrokerageRawPreviewAccount,
  BrokerageRawPreviewResponse,
} from '../../services/brokerageApi';

type LinkBrokerageMode = 'create-accounts' | 'link-existing-account';

interface LinkBrokerageModalProps {
  token?: string | null;
  existingAccountNames: string[];
  mode?: LinkBrokerageMode;
  targetAccountId?: string | null;
  targetAccountName?: string | null;
  targetAccountCompany?: string | null;
  targetAccountCompanyLabel?: string | null;
  onAccountsCreated: () => void;
  onExistingAccountLinked?: (accountId: string) => void | Promise<void>;
  onCancel: () => void;
  onClose: () => void;
  onStepChange: (isAccountSetupStep: boolean) => void;
  onTitleChange: (title: string) => void;
  resetToBrokerSelectionSignal: number;
}

interface LinkedAccountRow {
  snaptradeAccountId: string;
  displayLabel: string;
  accountType: string;
  accountValue: string | null;
  raw: unknown;
  selected: boolean;
  accountName: string;
  isHidden: boolean;
}

interface BrokerOption {
  slug: string;
  displayName: string;
  logoUrl: string;
  searchTerms?: string[];
}

type LinkStep = 'select-brokerage' | 'loading-accounts' | 'account-setup';

const PILOT_BROKERS: BrokerOption[] = [
  { slug: 'SCHWAB', displayName: 'Charles Schwab', logoUrl: '/company-schwab-logo.png', searchTerms: ['Schwab'] },
  { slug: 'COINBASE', displayName: 'Coinbase', logoUrl: '/company-coinbase-logo.png' },
  { slug: 'ETRADE', displayName: 'E*Trade', logoUrl: '/company-etrade-logo.png' },
  { slug: 'FIDELITY', displayName: 'Fidelity', logoUrl: '/company-fidelity-logo.jpg' },
  { slug: 'VANGUARD', displayName: 'Vanguard', logoUrl: '/company-vanguard-logo.png' },
];

const COMPANY_TO_SNAPTRADE_BROKER: Record<string, string> = {
  etrade: 'ETRADE',
  fidelity: 'FIDELITY',
  schwab: 'SCHWAB',
  coinbase: 'COINBASE',
  vanguard: 'VANGUARD',
};

const ACCOUNT_LOAD_RETRY_DELAYS_MS = [0, 1200, 2200, 3500, 5000];
const LINK_FLOW_STORAGE_KEY = 'vester:snaptrade-link-flow';

interface PersistedLinkFlow {
  brokerSlug?: string;
  mode?: LinkBrokerageMode;
  targetAccountId?: string | null;
  targetAccountName?: string | null;
  targetAccountCompany?: string | null;
  targetAccountCompanyLabel?: string | null;
  status?: 'selected' | 'portal-opened' | 'complete';
  completedAt?: number;
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asList(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (isRecord(payload)) {
    for (const key of ['accounts', 'data', 'results', 'body']) {
      if (Array.isArray(payload[key])) return payload[key];
    }
  }
  return [];
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function debugSnapTrade(message: string, data?: unknown): void {
  if (process.env.NODE_ENV === 'production') return;
  if (data === undefined) {
    console.debug(`[SnapTrade link] ${message}`);
  } else {
    console.debug(`[SnapTrade link] ${message}`, data);
  }
}

function summarizeNormalizationPreview(preview: BrokerageNormalizationPreviewResponse | null): unknown {
  if (!preview) return null;
  return {
    account_count: preview.account_count,
    generated_at: preview.generated_at,
    accounts: preview.accounts.map((account) => ({
      vester_account_id: account.vester_account.id,
      account_name: account.vester_account.account_name,
      company: account.vester_account.company,
      snaptrade_account_id: account.snaptrade_account_id,
      summary: account.summary,
    })),
  };
}

function extractCreatedVesterAccountIds(createdAccounts: unknown): string[] {
  return asList(createdAccounts)
    .map((account) => firstString(account, [['id'], ['account', 'id']]))
    .filter((accountId): accountId is string => Boolean(accountId));
}

async function copyJsonToClipboard(value: unknown): Promise<void> {
  const text = JSON.stringify(value, null, 2);
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard is not available in this browser.');
  }
  await navigator.clipboard.writeText(text);
}

function LoadingSpinner({ className = 'h-4 w-4 border-green-600' }: { className?: string }) {
  return <div className={`animate-spin rounded-full border-b-2 ${className}`} aria-hidden="true" />;
}

function readPersistedLinkFlow(): PersistedLinkFlow | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(LINK_FLOW_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isRecord(parsed) ? parsed as PersistedLinkFlow : null;
  } catch {
    return null;
  }
}

function writePersistedLinkFlow(flow: PersistedLinkFlow): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(LINK_FLOW_STORAGE_KEY, JSON.stringify(flow));
  } catch {
    // Session storage is best effort for preserving popup state across remounts.
  }
}

function clearPersistedLinkFlow(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(LINK_FLOW_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
}

function normalizeCompanyValue(company?: string | null): string {
  return (company || '').trim().toLowerCase();
}

function displayCompanyName(company?: string | null, fallback?: string | null): string {
  const trimmedFallback = fallback?.trim();
  if (trimmedFallback) return trimmedFallback;

  const normalized = normalizeCompanyValue(company);
  if (!normalized) return 'this company';
  const matchedBroker = PILOT_BROKERS.find((broker) => (
    normalizeCompanyValue(broker.slug) === normalized
    || normalizeCompanyValue(broker.displayName).replace(/[^a-z0-9]/g, '') === normalized.replace(/[^a-z0-9]/g, '')
  ));
  if (matchedBroker) return matchedBroker.displayName;
  return normalized
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'this company';
}

function initialBrokerSlug(): string {
  const persisted = readPersistedLinkFlow();
  const brokerSlug = typeof persisted?.brokerSlug === 'string' ? persisted.brokerSlug : '';
  return PILOT_BROKERS.some((broker) => broker.slug === brokerSlug) ? brokerSlug : '';
}

function initialStep(): LinkStep {
  const persisted = readPersistedLinkFlow();
  return persisted?.status === 'complete' ? 'loading-accounts' : 'select-brokerage';
}

function getPathString(value: unknown, path: string[]): string | null {
  let cursor = value;
  for (const key of path) {
    if (!isRecord(cursor)) return null;
    cursor = cursor[key];
  }
  if (cursor === null || cursor === undefined) return null;
  const text = String(cursor).trim();
  return text || null;
}

function firstString(value: unknown, paths: string[][]): string | null {
  for (const path of paths) {
    const found = getPathString(value, path);
    if (found) return found;
  }
  return null;
}

function normalizeName(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function uniqueName(baseName: string, usedNames: Set<string>): string {
  let candidate = baseName;
  let suffix = 2;
  while (usedNames.has(normalizeName(candidate))) {
    candidate = `${baseName} ${suffix}`;
    suffix += 1;
  }
  usedNames.add(normalizeName(candidate));
  return candidate;
}

function extractAccountId(account: unknown): string | null {
  return firstString(account, [
    ['id'],
    ['accountId'],
    ['account_id'],
    ['account', 'id'],
    ['account', 'accountId'],
    ['account', 'account_id'],
  ]);
}

function extractAccountConnectionId(account: unknown): string | null {
  return firstString(account, [
    ['brokerage_authorization'],
    ['brokerageAuthorization'],
    ['connection_id'],
    ['connectionId'],
    ['authorization_id'],
    ['authorizationId'],
    ['account', 'brokerage_authorization'],
    ['account', 'brokerageAuthorization'],
    ['account', 'connection_id'],
    ['account', 'connectionId'],
    ['account', 'authorization_id'],
    ['account', 'authorizationId'],
  ]);
}

function extractConnectionId(connection: unknown): string | null {
  return firstString(connection, [
    ['id'],
    ['connectionId'],
    ['connection_id'],
    ['authorizationId'],
    ['authorization_id'],
  ]);
}

function brokerValueMatches(value: string | null, broker: BrokerOption): boolean {
  if (!value) return false;
  const normalizedValue = value.toLowerCase();
  const candidates = [
    broker.slug,
    broker.displayName,
    ...(broker.searchTerms || []),
  ].map((item) => item.toLowerCase());
  return candidates.some((candidate) => (
    normalizedValue === candidate || normalizedValue.includes(candidate)
  ));
}

function connectionMatchesBroker(connection: unknown, broker: BrokerOption): boolean {
  return [
    firstString(connection, [['brokerage', 'slug'], ['brokerage_slug'], ['brokerageSlug']]),
    firstString(connection, [['brokerage', 'name'], ['brokerage_name'], ['brokerageName']]),
    firstString(connection, [['brokerage', 'display_name'], ['brokerage', 'displayName'], ['brokerage_display_name'], ['brokerageDisplayName']]),
    firstString(connection, [['name']]),
  ].some((value) => brokerValueMatches(value, broker));
}

function accountMatchesBroker(account: unknown, broker: BrokerOption): boolean {
  return [
    firstString(account, [['institution_name'], ['institution', 'name'], ['brokerage', 'name']]),
    firstString(account, [['account', 'institution_name'], ['account', 'institution', 'name'], ['account', 'brokerage', 'name']]),
  ].some((value) => brokerValueMatches(value, broker));
}

function filterAccountsForBroker(accountsPayload: unknown, connectionsPayload: unknown, broker: BrokerOption): unknown[] {
  const accounts = asList(accountsPayload);
  const matchingConnectionIds = new Set(
    asList(connectionsPayload)
      .filter((connection) => connectionMatchesBroker(connection, broker))
      .map(extractConnectionId)
      .filter((connectionId): connectionId is string => Boolean(connectionId))
  );

  if (matchingConnectionIds.size > 0) {
    return accounts.filter((account) => {
      const connectionId = extractAccountConnectionId(account);
      return connectionId ? matchingConnectionIds.has(connectionId) : accountMatchesBroker(account, broker);
    });
  }

  const brokerMatchedAccounts = accounts.filter((account) => accountMatchesBroker(account, broker));
  return brokerMatchedAccounts.length > 0 ? brokerMatchedAccounts : accounts;
}

function extractAccountType(account: unknown): string {
  return firstString(account, [
    ['account_category'],
    ['meta', 'type'],
    ['meta', 'accountType'],
    ['accountType'],
    ['account_type'],
    ['account', 'account_category'],
    ['account', 'meta', 'type'],
    ['account', 'meta', 'accountType'],
    ['account', 'accountType'],
    ['account', 'account_type'],
    ['account', 'type'],
    ['account', 'subtype'],
    ['account', 'classification'],
    ['type'],
    ['subtype'],
    ['classification'],
  ]) || 'Account';
}

function formatAccountType(accountType: string): string {
  const normalized = accountType.toLowerCase();
  if (normalized.includes('roth')) return 'Roth IRA';
  if (normalized.includes('ira') || normalized.includes('retirement')) return 'IRA';
  if (normalized.includes('brokerage') || normalized.includes('margin') || normalized.includes('cash')) {
    return 'Brokerage';
  }
  return accountType
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase()) || 'Account';
}

function extractDisplayLabel(account: unknown, brokerDisplayName: string, fallbackType: string): string {
  return firstString(account, [
    ['name'],
    ['accountName'],
    ['account_name'],
    ['number'],
    ['institution_account_id'],
    ['institution_name'],
    ['institution', 'name'],
    ['brokerage', 'name'],
    ['account', 'name'],
    ['account', 'accountName'],
    ['account', 'account_name'],
    ['account', 'number'],
    ['account', 'institution_account_id'],
    ['account', 'institution_name'],
    ['account', 'institution', 'name'],
    ['account', 'brokerage', 'name'],
  ]) || `${brokerDisplayName} ${fallbackType}`;
}

function formatCurrency(value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function rawPreviewSummary(account: BrokerageRawPreviewAccount): string {
  const summary = account.summary || {};
  const pieces = [
    `${summary.position_count ?? 0} position${summary.position_count === 1 ? '' : 's'}`,
    `${summary.tax_lot_count ?? 0} tax lot${summary.tax_lot_count === 1 ? '' : 's'}`,
    `${summary.activity_count ?? 0} activit${summary.activity_count === 1 ? 'y' : 'ies'}`,
    `${summary.activity_page_count ?? 0} page${summary.activity_page_count === 1 ? '' : 's'}`,
  ];
  if (summary.activity_truncated) pieces.push('truncated');
  return pieces.join(' - ');
}

function normalizationPreviewSummary(account: BrokerageNormalizationPreviewAccount): string {
  const summary = account.summary || {};
  const pieces = [
    `${summary.transactions_count ?? 0} transaction${summary.transactions_count === 1 ? '' : 's'}`,
    `${summary.cash_events_count ?? 0} cash event${summary.cash_events_count === 1 ? '' : 's'}`,
    `${summary.synthetic_opening_count ?? 0} synthetic opening${summary.synthetic_opening_count === 1 ? '' : 's'}`,
    `${summary.inferred_position_count ?? 0} inferred position${summary.inferred_position_count === 1 ? '' : 's'}`,
    `${summary.unknown_activity_transactions_count ?? 0} unknown`,
    `${summary.unmapped_activities_count ?? 0} unmapped`,
    `${summary.needs_review_count ?? 0} need${summary.needs_review_count === 1 ? 's' : ''} review`,
  ];
  return pieces.join(' - ');
}

function extractAccountValue(account: unknown): string | null {
  const paths = [
    ['balance', 'total', 'amount'],
    ['balance', 'amount'],
    ['balance'],
    ['balances', 'total', 'amount'],
    ['balances', 'total'],
    ['total_value', 'amount'],
    ['totalValue', 'amount'],
    ['value'],
    ['totalValue'],
    ['total_value'],
    ['account', 'balance', 'total', 'amount'],
    ['account', 'balance', 'amount'],
    ['account', 'balance'],
    ['account', 'balances', 'total', 'amount'],
    ['account', 'balances', 'total'],
    ['account', 'total_value', 'amount'],
    ['account', 'totalValue', 'amount'],
    ['account', 'value'],
    ['account', 'totalValue'],
    ['account', 'total_value'],
  ];

  for (const path of paths) {
    let cursor = account;
    for (const key of path) {
      if (!isRecord(cursor)) {
        cursor = null;
        break;
      }
      cursor = cursor[key];
    }
    const formatted = formatCurrency(cursor);
    if (formatted) return formatted;
  }
  return null;
}

function buildRows(
  accountsPayload: unknown[] | unknown,
  existingAccountNames: string[],
  brokerDisplayName: string
): LinkedAccountRow[] {
  const usedNames = new Set(existingAccountNames.map(normalizeName));
  return asList(accountsPayload)
    .map((account) => {
      const snaptradeAccountId = extractAccountId(account);
      if (!snaptradeAccountId) return null;

      const accountType = formatAccountType(extractAccountType(account));
      const baseName = `${brokerDisplayName} ${accountType}`;
      return {
        snaptradeAccountId,
        displayLabel: extractDisplayLabel(account, brokerDisplayName, accountType),
        accountType,
        accountValue: extractAccountValue(account),
        raw: account,
        selected: true,
        accountName: uniqueName(baseName, usedNames),
        isHidden: false,
      };
    })
    .filter((row): row is LinkedAccountRow => row !== null);
}

function validationErrors(rows: LinkedAccountRow[], existingAccountNames: string[]): Map<string, string> {
  const errors = new Map<string, string>();
  const existing = new Set(existingAccountNames.map(normalizeName));
  const selectedRows = rows.filter((row) => row.selected);
  const seen = new Map<string, string>();

  selectedRows.forEach((row) => {
    const normalized = normalizeName(row.accountName);
    if (!normalized) {
      errors.set(row.snaptradeAccountId, 'Account name is required');
      return;
    }
    if (existing.has(normalized)) {
      errors.set(row.snaptradeAccountId, 'This account name already exists');
      return;
    }
    if (seen.has(normalized)) {
      errors.set(row.snaptradeAccountId, 'This name is used more than once');
      const firstId = seen.get(normalized);
      if (firstId) errors.set(firstId, 'This name is used more than once');
      return;
    }
    seen.set(normalized, row.snaptradeAccountId);
  });

  return errors;
}

const LinkBrokerageModal: React.FC<LinkBrokerageModalProps> = ({
  token,
  existingAccountNames,
  mode = 'create-accounts',
  targetAccountId,
  targetAccountName,
  targetAccountCompany,
  targetAccountCompanyLabel,
  onAccountsCreated,
  onExistingAccountLinked,
  onCancel,
  onClose,
  onStepChange,
  onTitleChange,
  resetToBrokerSelectionSignal,
}) => {
  const [isOpening, setIsOpening] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [redirectUri, setRedirectUri] = useState<string | null>(null);
  const [rows, setRows] = useState<LinkedAccountRow[]>([]);
  const [selectedBrokerSlug, setSelectedBrokerSlug] = useState(initialBrokerSlug);
  const [brokerageSearchTerm, setBrokerageSearchTerm] = useState('');
  const [step, setStep] = useState<LinkStep>(initialStep);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [rawPreview, setRawPreview] = useState<BrokerageRawPreviewResponse | null>(null);
  const [isLoadingRawPreview, setIsLoadingRawPreview] = useState(false);
  const [rawPreviewError, setRawPreviewError] = useState<string | null>(null);
  const [normalizationPreview, setNormalizationPreview] = useState<BrokerageNormalizationPreviewResponse | null>(null);
  const [isLoadingNormalizationPreview, setIsLoadingNormalizationPreview] = useState(false);
  const [normalizationPreviewError, setNormalizationPreviewError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<BrokerageImportReviewedResponse | null>(null);
  const [isImportingReviewedData, setIsImportingReviewedData] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [hasCreatedAccounts, setHasCreatedAccounts] = useState(false);
  const [createdVesterAccountIds, setCreatedVesterAccountIds] = useState<string[]>([]);
  const didAutoResumeRef = useRef(false);
  const parentCallbacksRef = useRef({ onStepChange, onTitleChange });
  const lastParentStepRef = useRef<boolean | null>(null);
  const lastParentTitleRef = useRef<string | null>(null);
  const persistedLinkFlow = readPersistedLinkFlow();
  const persistedExistingContext = persistedLinkFlow?.mode === 'link-existing-account'
    && persistedLinkFlow.targetAccountId
    && (persistedLinkFlow.status === 'portal-opened' || persistedLinkFlow.status === 'complete')
    ? persistedLinkFlow
    : null;
  const effectiveMode = mode === 'link-existing-account' || persistedExistingContext
    ? 'link-existing-account'
    : mode;
  const effectiveTargetAccountId = targetAccountId || persistedExistingContext?.targetAccountId || null;
  const effectiveTargetAccountName = targetAccountName || persistedExistingContext?.targetAccountName || null;
  const effectiveTargetAccountCompany = targetAccountCompany || persistedExistingContext?.targetAccountCompany || null;
  const effectiveTargetAccountCompanyLabel = targetAccountCompanyLabel || persistedExistingContext?.targetAccountCompanyLabel || null;
  const isExistingAccountMode = effectiveMode === 'link-existing-account';
  const targetCompanyKey = normalizeCompanyValue(effectiveTargetAccountCompany);
  const requiredBrokerSlug = isExistingAccountMode ? COMPANY_TO_SNAPTRADE_BROKER[targetCompanyKey] || null : null;
  const targetCompanyDisplayName = displayCompanyName(effectiveTargetAccountCompany, effectiveTargetAccountCompanyLabel);
  const requiredBroker = useMemo(
    () => PILOT_BROKERS.find((broker) => broker.slug === requiredBrokerSlug) || null,
    [requiredBrokerSlug]
  );
  const targetCompanyLinkingUnavailable = isExistingAccountMode && !requiredBroker;
  const availableBrokers = useMemo(
    () => (isExistingAccountMode ? (requiredBroker ? [requiredBroker] : []) : PILOT_BROKERS),
    [isExistingAccountMode, requiredBroker]
  );

  const selectedBroker = useMemo(
    () => availableBrokers.find((broker) => broker.slug === selectedBrokerSlug) || null,
    [availableBrokers, selectedBrokerSlug]
  );
  const selectedBrokerDisplayName = selectedBroker?.displayName || null;

  const filteredBrokers = useMemo(() => {
    if (isExistingAccountMode) return availableBrokers;

    const query = brokerageSearchTerm.trim().toLowerCase();
    if (!query) return availableBrokers;

    return availableBrokers.filter((broker) => (
      [
        broker.displayName,
        broker.slug,
        ...(broker.searchTerms || []),
      ].some((value) => value.toLowerCase().includes(query))
    ));
  }, [availableBrokers, brokerageSearchTerm, isExistingAccountMode]);

  const errorsById = useMemo(
    () => (hasCreatedAccounts || isExistingAccountMode ? new Map<string, string>() : validationErrors(rows, existingAccountNames)),
    [hasCreatedAccounts, isExistingAccountMode, rows, existingAccountNames]
  );
  const selectedRows = useMemo(
    () => rows.filter((row) => row.selected),
    [rows]
  );
  const selectedSnaptradeAccountIds = useMemo(
    () => selectedRows.map((row) => row.snaptradeAccountId),
    [selectedRows]
  );
  const canSubmit = !!selectedBroker
    && selectedRows.length > 0
    && (!isExistingAccountMode || Boolean(effectiveTargetAccountId))
    && errorsById.size === 0
    && !isCreating
    && !hasCreatedAccounts;
  const isAccountSetupStep = step !== 'select-brokerage';
  const canImportReviewedData = !!normalizationPreview
    && normalizationPreview.accounts.length > 0
    && !isImportingReviewedData;

  const previewAccountScope = useCallback((vesterAccountIdsOverride?: string[]) => {
    const vesterAccountIds = vesterAccountIdsOverride || createdVesterAccountIds;
    if (vesterAccountIds.length > 0) return { vesterAccountIds };
    return { snaptradeAccountIds: selectedSnaptradeAccountIds };
  }, [createdVesterAccountIds, selectedSnaptradeAccountIds]);

  useEffect(() => {
    parentCallbacksRef.current = { onStepChange, onTitleChange };
  }, [onStepChange, onTitleChange]);

  const persistCurrentLinkFlow = useCallback((flow: PersistedLinkFlow) => {
    writePersistedLinkFlow({
      brokerSlug: flow.brokerSlug,
      mode: isExistingAccountMode ? 'link-existing-account' : 'create-accounts',
      targetAccountId: isExistingAccountMode ? effectiveTargetAccountId : null,
      targetAccountName: isExistingAccountMode ? effectiveTargetAccountName : null,
      targetAccountCompany: isExistingAccountMode ? effectiveTargetAccountCompany : null,
      targetAccountCompanyLabel: isExistingAccountMode ? effectiveTargetAccountCompanyLabel : null,
      status: flow.status,
      completedAt: flow.completedAt,
    });
  }, [
    effectiveTargetAccountCompany,
    effectiveTargetAccountCompanyLabel,
    effectiveTargetAccountId,
    effectiveTargetAccountName,
    isExistingAccountMode,
  ]);

  useEffect(() => {
    if (!isExistingAccountMode) return;

    setBrokerageSearchTerm('');
    if (requiredBrokerSlug) {
      setSelectedBrokerSlug(requiredBrokerSlug);
      const persisted = readPersistedLinkFlow();
      if (persisted?.brokerSlug && persisted.brokerSlug !== requiredBrokerSlug) {
        clearPersistedLinkFlow();
        setStep('select-brokerage');
      }
      return;
    }

    setSelectedBrokerSlug('');
    setStep('select-brokerage');
    clearPersistedLinkFlow();
  }, [isExistingAccountMode, requiredBrokerSlug]);

  useEffect(() => {
    const nextIsAccountSetupStep = Boolean(isAccountSetupStep && selectedBrokerDisplayName);
    const existingAccountTitle = `Link '${effectiveTargetAccountName || 'account'}' to External Account`;
    const nextTitle = isExistingAccountMode
      ? existingAccountTitle
      : nextIsAccountSetupStep
        ? `Create ${selectedBrokerDisplayName} account(s)`
        : 'Link Investments';

    if (lastParentStepRef.current !== nextIsAccountSetupStep) {
      lastParentStepRef.current = nextIsAccountSetupStep;
      parentCallbacksRef.current.onStepChange(nextIsAccountSetupStep);
    }

    if (lastParentTitleRef.current !== nextTitle) {
      lastParentTitleRef.current = nextTitle;
      parentCallbacksRef.current.onTitleChange(nextTitle);
    }
  }, [effectiveTargetAccountName, isAccountSetupStep, isExistingAccountMode, selectedBrokerDisplayName]);

  useEffect(() => {
    if (resetToBrokerSelectionSignal === 0) return;

    clearPersistedLinkFlow();
    setError(null);
    setSuccess(null);
    setRedirectUri(null);
    setRows([]);
    setRawPreview(null);
    setRawPreviewError(null);
    setNormalizationPreview(null);
    setNormalizationPreviewError(null);
    setImportResult(null);
    setImportError(null);
    setHasCreatedAccounts(false);
    setCreatedVesterAccountIds([]);
    setBrokerageSearchTerm('');
    setStep('select-brokerage');
    setIsLoadingAccounts(false);
  }, [resetToBrokerSelectionSignal]);

  const loadRawPreview = useCallback(async (vesterAccountIdsOverride?: string[]) => {
    if (!token) {
      setRawPreviewError('Your session expired. Please sign in again.');
      return null;
    }

    const accountScope = previewAccountScope(vesterAccountIdsOverride);
    if (!accountScope.vesterAccountIds?.length && !accountScope.snaptradeAccountIds?.length) {
      setRawPreviewError('Select at least one account first.');
      return null;
    }

    setRawPreviewError(null);
    setIsLoadingRawPreview(true);
    try {
      const preview = await getRawAccountPreview(token, {
        activityLimit: 1000,
        maxPages: 50,
        ...accountScope,
      });
      setRawPreview(preview);
      debugSnapTrade('Loaded raw SnapTrade account preview', preview);
      return preview;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load raw brokerage data.';
      setRawPreviewError(message);
      return null;
    } finally {
      setIsLoadingRawPreview(false);
    }
  }, [previewAccountScope, token]);

  const loadNormalizationPreview = useCallback(async () => {
    if (!token) {
      setNormalizationPreviewError('Your session expired. Please sign in again.');
      return null;
    }

    const accountScope = previewAccountScope();
    if (!accountScope.vesterAccountIds?.length && !accountScope.snaptradeAccountIds?.length) {
      setNormalizationPreviewError('Select at least one account first.');
      return null;
    }

    setNormalizationPreviewError(null);
    setIsLoadingNormalizationPreview(true);
    try {
      const preview = await getNormalizationPreview(token, {
        activityLimit: 1000,
        maxPages: 50,
        ...accountScope,
      });
      setNormalizationPreview(preview);
      setImportResult(null);
      setImportError(null);
      debugSnapTrade('Loaded SnapTrade normalization preview', preview);
      return preview;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load normalization preview.';
      setNormalizationPreviewError(message);
      return null;
    } finally {
      setIsLoadingNormalizationPreview(false);
    }
  }, [previewAccountScope, token]);

  const importReviewedData = useCallback(async () => {
    if (!token) {
      setImportError('Your session expired. Please sign in again.');
      return;
    }

    const accountScope = previewAccountScope();
    if (!accountScope.vesterAccountIds?.length && !accountScope.snaptradeAccountIds?.length) {
      setImportError('Select at least one account first.');
      return;
    }

    setImportError(null);
    setIsImportingReviewedData(true);
    debugSnapTrade('Import reviewed data started', {
      broker: selectedBroker?.slug || null,
      preview: summarizeNormalizationPreview(normalizationPreview),
    });
    try {
      const result = await importReviewedBrokerageData(token, {
        activityLimit: 1000,
        maxPages: 50,
        ...accountScope,
      });
      setImportResult(result);
      debugSnapTrade('Import reviewed data completed', {
        broker: selectedBroker?.slug || null,
        imported_count: result.imported_count,
        blocked_count: result.blocked_count,
        accounts: (result.accounts || []).map((account) => ({
          vester_account_id: account.vester_account.id,
          account_name: account.vester_account.account_name,
          snaptrade_account_id: account.snaptrade_account_id,
          summary: account.summary,
          upserted: account.upserted,
          recalculate: account.recalculate,
        })),
        result,
      });
      toast.success(`Imported brokerage data for ${result.imported_count} account${result.imported_count === 1 ? '' : 's'}.`);
      onAccountsCreated();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to import reviewed brokerage data.';
      debugSnapTrade('Import reviewed data failed', {
        broker: selectedBroker?.slug || null,
        message,
        preview: summarizeNormalizationPreview(normalizationPreview),
      });
      setImportError(message);
    } finally {
      setIsImportingReviewedData(false);
    }
  }, [normalizationPreview, onAccountsCreated, previewAccountScope, selectedBroker?.slug, token]);

  const loadSnapTradeData = useCallback(async () => {
    if (!token) {
      setError('Your session expired. Please sign in again.');
      return;
    }
    if (!selectedBroker) {
      setError('Select a brokerage first.');
      return;
    }

    setError(null);
    setSuccess(null);
    setStep('loading-accounts');
    setIsLoadingAccounts(true);
    setHasCreatedAccounts(false);
    try {
      let lastRows: LinkedAccountRow[] = [];
      let lastAccountCount = 0;

      for (let attemptIndex = 0; attemptIndex < ACCOUNT_LOAD_RETRY_DELAYS_MS.length; attemptIndex += 1) {
        const delayMs = ACCOUNT_LOAD_RETRY_DELAYS_MS[attemptIndex];
        if (delayMs > 0) await sleep(delayMs);

        const [connectionsResponse, accountsResponse] = await Promise.all([
          getConnections(token),
          getAccounts(token),
        ]);
        const accountList = filterAccountsForBroker(
          accountsResponse.accounts,
          connectionsResponse.connections,
          selectedBroker
        );
        const parsedRows = buildRows(
          accountList,
          existingAccountNames,
          selectedBroker.displayName
        );
        const modeRows = isExistingAccountMode
          ? parsedRows.map((row, index) => ({
              ...row,
              selected: index === 0,
              accountName: effectiveTargetAccountName || row.accountName,
            }))
          : parsedRows;

        lastRows = modeRows;
        lastAccountCount = accountList.length;

        debugSnapTrade('Loaded SnapTrade account data', {
          attempt: attemptIndex + 1,
          connectionCount: asList(connectionsResponse.connections).length,
          accountCount: accountList.length,
          parsedRowCount: modeRows.length,
          selectedBroker: selectedBroker.slug,
          accountsPayload: accountList,
        });

        if (modeRows.length > 0 || accountList.length > 0) break;
      }

      setRows(lastRows);
      setStep('account-setup');

      if (lastAccountCount > 0 && lastRows.length === 0) {
        setError('SnapTrade returned accounts, but Vester could not identify account IDs in the response.');
      } else if (lastAccountCount === 0) {
        setError('SnapTrade connected, but no brokerage accounts were returned yet. Wait a minute and reconnect or reopen this modal.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load linked accounts.');
      setStep('account-setup');
    } finally {
      setIsLoadingAccounts(false);
    }
  }, [token, existingAccountNames, effectiveTargetAccountName, isExistingAccountMode, selectedBroker]);

  useEffect(() => {
    const handlePayload = (payload: any) => {
      if (!payload || payload.type !== 'snaptrade-link-complete') return;
      if (payload.error) {
        setError(payload.error);
        return;
      }
      debugSnapTrade('Received SnapTrade callback payload', payload);
      if (selectedBroker) {
        persistCurrentLinkFlow({
          brokerSlug: selectedBroker.slug,
          status: 'complete',
          completedAt: Date.now(),
        });
      }
      setSuccess(`${selectedBroker?.displayName || 'Brokerage'} connected.`);
      loadSnapTradeData();
    };

    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel(SNAPTRADE_LINK_CHANNEL);
      channel.onmessage = (event) => handlePayload(event.data);
    } catch {
      channel = null;
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== SNAPTRADE_LINK_CHANNEL || !event.newValue) return;
      try {
        handlePayload(JSON.parse(event.newValue));
      } catch {
        // Ignore malformed callback payloads.
      }
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      if (channel) channel.close();
      window.removeEventListener('storage', handleStorage);
    };
  }, [loadSnapTradeData, persistCurrentLinkFlow, selectedBroker]);

  useEffect(() => {
    if (didAutoResumeRef.current || !selectedBroker || !token) return;

    const persisted = readPersistedLinkFlow();
    if (persisted?.status !== 'complete' || persisted.brokerSlug !== selectedBroker.slug) return;

    didAutoResumeRef.current = true;
    debugSnapTrade('Resuming completed SnapTrade link flow after remount', persisted);
    setStep('loading-accounts');
    loadSnapTradeData();
  }, [loadSnapTradeData, selectedBroker, token]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const status = searchParams.get('status');
    const connectionId = searchParams.get('connection_id') || searchParams.get('connectionId');
    const callbackError = searchParams.get('error') || searchParams.get('error_message');
    if (!status && !connectionId && !callbackError) return;

    notifySnapTradeLinkComplete({
      type: 'snaptrade-link-complete',
      status: status || '',
      connectionId: connectionId || '',
      error: callbackError || '',
    });

    const timer = window.setTimeout(() => {
      window.close();
    }, 400);

    return () => window.clearTimeout(timer);
  }, []);

  const handleOpenPortal = async () => {
    setError(null);
    setSuccess(null);
    setRedirectUri(null);
    setRawPreview(null);
    setRawPreviewError(null);
    setNormalizationPreview(null);
    setNormalizationPreviewError(null);
    setImportResult(null);
    setImportError(null);
    setHasCreatedAccounts(false);
    setCreatedVesterAccountIds([]);

    if (!selectedBroker) {
      setError('Select a brokerage first.');
      return;
    }

    const popup = window.open('', 'vester-snaptrade-link', 'width=520,height=760');

    if (!token) {
      if (popup) popup.close();
      setError('Your session expired. Please sign in again.');
      return;
    }

    try {
      setIsOpening(true);
      persistCurrentLinkFlow({
        brokerSlug: selectedBroker.slug,
        status: 'portal-opened',
      });
      const session = await createLinkSession(token, selectedBroker.slug);
      setRedirectUri(session.redirectURI);

      if (popup) {
        popup.location.href = session.redirectURI;
      }
    } catch (err) {
      if (popup) popup.close();
      setError(err instanceof Error ? err.message : 'Failed to open brokerage linking.');
    } finally {
      setIsOpening(false);
    }
  };

  const updateRow = (accountId: string, patch: Partial<LinkedAccountRow>) => {
    setRows((current) => current.map((row) => (
      row.snaptradeAccountId === accountId ? { ...row, ...patch } : row
    )));
  };

  const selectSingleRow = (accountId: string) => {
    setRows((current) => current.map((row) => ({
      ...row,
      selected: row.snaptradeAccountId === accountId,
    })));
  };

  const handleCancel = () => {
    clearPersistedLinkFlow();
    onCancel();
  };

  const handlePrimaryAction = async () => {
    if (!token) {
      setError('Your session expired. Please sign in again.');
      return;
    }
    if (!canSubmit) return;

    setError(null);
    setSuccess(null);
    setIsCreating(true);
    try {
      if (isExistingAccountMode) {
        const selectedRow = selectedRows[0];
        if (!effectiveTargetAccountId || !selectedRow || !selectedBroker) {
          setError('Select one external account to link.');
          return;
        }

        await linkExistingBrokerageAccount(
          token,
          effectiveTargetAccountId,
          selectedBroker.slug,
          selectedRow.snaptradeAccountId
        );
        clearPersistedLinkFlow();
        await onExistingAccountLinked?.(effectiveTargetAccountId);
        return;
      }

      const response = await createSelectedBrokerageAccounts(
        token,
        selectedBroker?.slug || '',
        selectedRows.map((row) => ({
          snaptrade_account_id: row.snaptradeAccountId,
          account_name: row.accountName.trim(),
          is_hidden: row.isHidden,
          raw: row.raw,
        }))
      );
      const message = `${response.created_count} account${response.created_count === 1 ? '' : 's'} created.`;
      const createdIds = extractCreatedVesterAccountIds(response.created_accounts);
      setSuccess(message);
      setHasCreatedAccounts(true);
      setCreatedVesterAccountIds(createdIds);
      toast.success(message);
      clearPersistedLinkFlow();
      onAccountsCreated();
      await loadRawPreview(createdIds);
    } catch (err) {
      setError(err instanceof Error ? err.message : isExistingAccountMode ? 'Failed to link account.' : 'Failed to create accounts.');
    } finally {
      setIsCreating(false);
    }
  };

  if (isAccountSetupStep) {
    return (
      <div className="space-y-5">
        <>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {success && (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
              {success}
            </div>
          )}

          {isLoadingAccounts ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 p-4 text-sm text-gray-500">
              <LoadingSpinner />
              Loading linked accounts...
            </div>
          ) : rows.length > 0 ? (
            <>
              <div className="space-y-3">
                {rows.map((row) => {
                  const rowError = errorsById.get(row.snaptradeAccountId);
                  return (
                    <div key={row.snaptradeAccountId} className="rounded-lg border border-gray-200 p-3">
                      <div className={`grid items-start gap-3 ${isExistingAccountMode ? 'grid-cols-[32px_minmax(0,1fr)]' : 'grid-cols-[32px_minmax(0,1fr)_40px]'}`}>
                        <input
                          type={isExistingAccountMode ? 'radio' : 'checkbox'}
                          name={isExistingAccountMode ? 'snaptrade-account-to-link' : undefined}
                          checked={row.selected}
                          onChange={(event) => {
                            if (isExistingAccountMode) {
                              selectSingleRow(row.snaptradeAccountId);
                            } else {
                              updateRow(row.snaptradeAccountId, { selected: event.target.checked });
                            }
                          }}
                          className="mt-3 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                          aria-label={isExistingAccountMode ? `Link ${row.displayLabel}` : `Create ${row.accountName}`}
                        />
                        <div className="min-w-0 space-y-2">
                          <div>
                            <div className="truncate text-sm font-medium text-gray-900">{row.displayLabel}</div>
                            <div className="text-xs text-gray-500">
                              {row.accountType}{row.accountValue ? ` - ${row.accountValue}` : ''}
                            </div>
                          </div>
                          {!isExistingAccountMode && (
                            <>
                              <input
                                type="text"
                                value={row.accountName}
                                onChange={(event) => updateRow(row.snaptradeAccountId, { accountName: event.target.value })}
                                disabled={!row.selected}
                                className={`block w-full rounded-md border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-green-500 ${
                                  rowError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-green-500'
                                } disabled:bg-gray-100 disabled:text-gray-400`}
                                aria-label="Vester account name"
                              />
                              {rowError && <p className="text-xs text-red-600">{rowError}</p>}
                            </>
                          )}
                        </div>
                        {!isExistingAccountMode && (
                          <button
                            type="button"
                            onClick={() => updateRow(row.snaptradeAccountId, { isHidden: !row.isHidden })}
                            className={`flex h-10 w-10 items-center justify-center rounded-md border ${
                              row.isHidden
                                ? 'border-gray-300 bg-gray-100 text-gray-500'
                                : 'border-transparent text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                            title={row.isHidden ? 'Hidden from others' : 'Visible to others'}
                            aria-label={row.isHidden ? 'Hidden from others' : 'Visible to others'}
                          >
                            <img
                              src={row.isHidden ? '/icon-unhide-lrg.png' : '/icon-hide-lrg.png'}
                              alt=""
                              className="h-5 w-5"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isCreating}
                  className="rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handlePrimaryAction}
                  disabled={!canSubmit}
                  className="flex items-center justify-center rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {isCreating && (
                    <LoadingSpinner className="-ml-1 mr-2 h-4 w-4 border-white" />
                  )}
                  {isExistingAccountMode
                    ? isCreating ? 'Linking account...' : 'Link to account'
                    : isCreating ? 'Creating account(s)' : 'Create account(s)'}
                </button>
              </div>

              {!isExistingAccountMode && (
              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Raw brokerage data</div>
                    {rawPreview?.generated_at && (
                      <div className="text-xs text-gray-500">
                        Loaded {formatDateTime(rawPreview.generated_at)}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => loadRawPreview()}
                    disabled={isLoadingRawPreview || !token}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {isLoadingRawPreview && (
                      <LoadingSpinner className="-ml-1 mr-2 h-3 w-3 border-gray-500" />
                    )}
                    {isLoadingRawPreview ? 'Loading raw data...' : 'Load raw data'}
                  </button>
                </div>

                {rawPreviewError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {rawPreviewError}
                  </div>
                )}

                {rawPreview && rawPreview.accounts.length === 0 && (
                  <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500">
                    No created SnapTrade-backed Vester accounts are available for raw preview.
                  </div>
                )}

                {rawPreview && rawPreview.accounts.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await copyJsonToClipboard(rawPreview);
                            toast.success('Copied raw brokerage payload.');
                          } catch (err) {
                            toast.error(err instanceof Error ? err.message : 'Could not copy raw payload.');
                          }
                        }}
                        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        Copy all JSON
                      </button>
                      <div className="text-xs text-gray-500">
                        {rawPreview.account_count} account{rawPreview.account_count === 1 ? '' : 's'} - {rawPreview.activity_limit} activities per page - {rawPreview.max_pages} page max
                      </div>
                    </div>

                    {rawPreview.accounts.map((previewAccount) => (
                      <div
                        key={`${previewAccount.vester_account.id || ''}-${previewAccount.snaptrade_account_id}`}
                        className="rounded-md border border-gray-200 bg-white p-3"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {previewAccount.vester_account.account_name || previewAccount.snaptrade_account_id}
                            </div>
                            <div className="break-all text-xs text-gray-500">
                              {previewAccount.snaptrade_account_id}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {rawPreviewSummary(previewAccount)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await copyJsonToClipboard(previewAccount);
                                toast.success('Copied account raw JSON.');
                              } catch (err) {
                                toast.error(err instanceof Error ? err.message : 'Could not copy account JSON.');
                              }
                            }}
                            className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                          >
                            Copy account JSON
                          </button>
                        </div>

                        <details className="mt-3">
                          <summary className="cursor-pointer text-xs font-medium text-gray-700">
                            View raw JSON
                          </summary>
                          <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-gray-900 p-3 text-xs leading-5 text-gray-100">
                            {JSON.stringify(previewAccount, null, 2)}
                          </pre>
                        </details>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {!isExistingAccountMode && (
              <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">Normalization preview</div>
                    {normalizationPreview?.generated_at && (
                      <div className="text-xs text-gray-500">
                        Loaded {formatDateTime(normalizationPreview.generated_at)}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => loadNormalizationPreview()}
                    disabled={isLoadingNormalizationPreview || !token}
                    className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                  >
                    {isLoadingNormalizationPreview && (
                      <LoadingSpinner className="-ml-1 mr-2 h-3 w-3 border-gray-500" />
                    )}
                    {isLoadingNormalizationPreview ? 'Loading preview...' : 'Load normalization preview'}
                  </button>
                </div>

                {normalizationPreviewError && (
                  <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {normalizationPreviewError}
                  </div>
                )}

                {normalizationPreview && normalizationPreview.accounts.length === 0 && (
                  <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500">
                    No created SnapTrade-backed Vester accounts are available for normalization preview.
                  </div>
                )}

                {normalizationPreview && normalizationPreview.accounts.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await copyJsonToClipboard(normalizationPreview);
                            toast.success('Copied normalization preview.');
                          } catch (err) {
                            toast.error(err instanceof Error ? err.message : 'Could not copy normalization preview.');
                          }
                        }}
                        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        Copy all JSON
                      </button>
                      <div className="text-xs text-gray-500">
                        {normalizationPreview.account_count} account{normalizationPreview.account_count === 1 ? '' : 's'} - dry run only
                      </div>
                    </div>

                    {normalizationPreview.accounts.map((previewAccount) => (
                      <div
                        key={`${previewAccount.vester_account.id || ''}-${previewAccount.snaptrade_account_id}-normalization`}
                        className="rounded-md border border-gray-200 bg-white p-3"
                      >
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {previewAccount.vester_account.account_name || previewAccount.snaptrade_account_id}
                            </div>
                            <div className="break-all text-xs text-gray-500">
                              {previewAccount.snaptrade_account_id}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {normalizationPreviewSummary(previewAccount)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await copyJsonToClipboard(previewAccount);
                                toast.success('Copied account normalization JSON.');
                              } catch (err) {
                                toast.error(err instanceof Error ? err.message : 'Could not copy account normalization JSON.');
                              }
                            }}
                            className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                          >
                            Copy account JSON
                          </button>
                        </div>

                        <details className="mt-3">
                          <summary className="cursor-pointer text-xs font-medium text-gray-700">
                            View proposed rows
                          </summary>
                          <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-gray-900 p-3 text-xs leading-5 text-gray-100">
                            {JSON.stringify(previewAccount, null, 2)}
                          </pre>
                        </details>
                      </div>
                    ))}

                    {importError && (
                      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                        {importError}
                      </div>
                    )}

                    {importResult && (
                      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                        Imported {importResult.imported_count} account{importResult.imported_count === 1 ? '' : 's'} and queued recalculation.
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={importReviewedData}
                      disabled={!canImportReviewedData}
                      className="flex w-full items-center justify-center rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      {isImportingReviewedData && (
                        <LoadingSpinner className="-ml-1 mr-2 h-4 w-4 border-white" />
                      )}
                      {isImportingReviewedData ? 'Importing reviewed data...' : 'Import reviewed data'}
                    </button>

                    {normalizationPreview.accounts.some((account) => (
                      (account.summary.unknown_activity_transactions_count ?? 0) > 0
                      || (account.summary.unmapped_activities_count ?? 0) > 0
                    )) && (
                      <div className="text-xs text-amber-700">
                        Unrecognized brokerage activity imports as UNKNOWN review rows so it can be reviewed and mapped later.
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-gray-200 p-4 text-sm text-gray-500">
              {isExistingAccountMode
                ? 'No linked accounts are available to select yet.'
                : 'No linked accounts are available to create yet.'}
            </div>
          )}

        </>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <>
        <div className="space-y-3">
          <div>
            {!isExistingAccountMode && (
              <>
                <label htmlFor="brokerage-search" className="sr-only">
                  Search
                </label>
                <input
                  id="brokerage-search"
                  type="search"
                  value={brokerageSearchTerm}
                  onChange={(event) => setBrokerageSearchTerm(event.target.value)}
                  placeholder="Search"
                  className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {filteredBrokers.map((broker) => {
              const isSelected = selectedBrokerSlug === broker.slug;
              return (
                <button
                  key={broker.slug}
                  type="button"
                  onClick={() => {
                    setSelectedBrokerSlug(broker.slug);
                    persistCurrentLinkFlow({
                      brokerSlug: broker.slug,
                      status: 'selected',
                    });
                    setSuccess(null);
                    setError(null);
                    setRedirectUri(null);
                    setRows([]);
                    setRawPreview(null);
                    setRawPreviewError(null);
                    setNormalizationPreview(null);
                    setNormalizationPreviewError(null);
                    setImportResult(null);
                    setImportError(null);
                    setHasCreatedAccounts(false);
                    setStep('select-brokerage');
                  }}
                  className={`rounded-lg border p-4 text-left transition ${
                    isSelected
                      ? 'border-green-500 bg-green-50 ring-2 ring-green-100'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <img src={broker.logoUrl} alt="" className="h-9 w-9 rounded" />
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{broker.displayName}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          {filteredBrokers.length === 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
              {targetCompanyLinkingUnavailable
                ? `SnapTrade linking is not available for ${targetCompanyDisplayName} yet.`
                : 'No brokerages match your search.'}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">
          {targetCompanyLinkingUnavailable
            ? `This account is set up as ${targetCompanyDisplayName}, but that company is not enabled for SnapTrade linking yet.`
            : isExistingAccountMode
              ? `This account is set up as ${targetCompanyDisplayName}, so it can only be linked to ${selectedBrokerDisplayName || targetCompanyDisplayName}.`
              : 'Pick a brokerage first. The connection will open in a SnapTrade popup.'}
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
            {success}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg border border-gray-300 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleOpenPortal}
            disabled={isOpening || !token || !selectedBroker || targetCompanyLinkingUnavailable}
            className="rounded-lg bg-green-600 px-4 py-3 text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isOpening ? `Opening ${selectedBroker?.displayName || 'brokerage'}...` : selectedBroker ? `Connect to ${selectedBroker.displayName}` : 'Connect'}
          </button>
        </div>

        {redirectUri && (
          <a
            href={redirectUri}
            target="_blank"
            rel="noreferrer"
            className="block rounded-lg border border-gray-300 px-4 py-3 text-center text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Open SnapTrade in new tab
          </a>
        )}
      </>
    </div>
  );
};

export default LinkBrokerageModal;
